import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { NetworkConstants } from 'diwimamodelweb/lib';

@Injectable({ providedIn: 'root' })
export class ContextNameLookupService {

  private firestore = inject(Firestore);
  private zone = inject(NgZone);

  private cache$ = new BehaviorSubject<Record<string, string>>({});
  private inflight = new Set<string>();

  names$: Observable<Record<string, string>> = this.cache$.asObservable();

  get currentNames(): Record<string, string> {
    return this.cache$.value;
  }

  nameOf(contextId: string | null | undefined): string | null {
    if (!contextId) return null;
    return this.cache$.value[contextId] || null;
  }

  ensureLoaded(contextIds: (string | null | undefined)[]): void {
    const cached = this.cache$.value;
    const toFetch = [...new Set(contextIds)]
      .filter((id): id is string => !!id && !(id in cached) && !this.inflight.has(id));
    if (!toFetch.length) return;

    toFetch.forEach(id => this.inflight.add(id));

    Promise.all(toFetch.map(async id => {
      try {
        const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_CONTEXT, id));
        const name = snap.exists() ? ((snap.data() as any)?.name ?? '') : '';
        return [id, name] as const;
      } catch (err) {
        console.error(`[ContextNameLookup] Failed to read Context/${id}:`, err);
        return [id, ''] as const;
      } finally {
        this.inflight.delete(id);
      }
    })).then(results => {

      this.zone.run(() => {
        const next = { ...this.cache$.value };

        results.forEach(([id, name]) => { next[id] = name; });
        this.cache$.next(next);
      });
    });
  }
}